import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BullhornAuthService, InveniasAuthService, SalesforceAuthService } from 'auth-service-lib';
import { Namespace } from 'enums-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { IdentityStorage, SessionInfoStorage } from 'storage-service-lib';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: false
})
export class SignInComponent implements OnInit {
  lastUsedTenant: string = undefined;
  useSalesforceSandbox = false;

  bullhornSigninInProgress = signal<boolean>(false);
  inveniasSigninInProgress = signal<boolean>(false);
  salesForceSigninInProgress = signal<boolean>(false);

  autoSignInInProgress = false;
  signinInProgress = computed(() => {
    return this.bullhornSigninInProgress() || this.salesForceSigninInProgress() || this.inveniasSigninInProgress();
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private identityStorage: IdentityStorage,
    private sessionInfoStorage: SessionInfoStorage,
    private messageNotificationService: MessageNotificationService,
    private bullhornAuthService: BullhornAuthService,
    private salesforceAuthService: SalesforceAuthService,
    private inveniasAuthService: InveniasAuthService
  ) {
    console.log('SignInComponent created.');
  }

  ngOnInit(): void {
    const namespace = this.route.snapshot.queryParams['namespace']?.toLowerCase();
    const tenant = this.route.snapshot.queryParams['tenant']?.toLowerCase();
    if (tenant && tenant.trim() !== '') {
      localStorage.setItem('lastUsedTenant', tenant);
    }
    this.lastUsedTenant = localStorage.getItem('lastUsedTenant') || '';
    this.identityStorage.clear();

    if (namespace) {
      this.autoSignIn(namespace, tenant);
    }
  }

  clearInput(input: HTMLInputElement, event?: any): void {
    input.value = '';
    input.focus();
    event?.preventDefault();
  }

  signInWithBullhorn(): void {
    this.bullhornSigninInProgress.set(true);
    this.bullhornAuthService.getToken().subscribe({
      next: () => (this.bullhornSigninInProgress.set(false)),
      error: () => (this.bullhornSigninInProgress.set(false)),
    });
  }

  signInWithSalesforce(): void {
    this.salesForceSigninInProgress.set(true);
    const sandboxEnv = this.route.snapshot.queryParams['env']?.toLowerCase()?.includes('sandbox');
    if (sandboxEnv) {
      this.useSalesforceSandbox = sandboxEnv;
    }
    if (this.useSalesforceSandbox) {
      this.sessionInfoStorage.put({ signInUrl: `${window.location.origin}/signin?env=sandbox` });
    }
    this.salesforceAuthService.getToken().subscribe({
      next: () => (this.salesForceSigninInProgress.set(false)),
      error: () => (this.salesForceSigninInProgress.set(false)),
    });
  }

  signInWithInvenias(tenantName: string): void {
    this.inveniasSigninInProgress.set(true);
    if (tenantName === '') {
      this.messageNotificationService.error('Invalid tenant', 'Please enter your Invenias tenant name.');
      return;
    }

    localStorage.setItem('lastUsedTenant', tenantName);
    this.identityStorage.put({ namespace: Namespace.invenias, tenant: tenantName, token: null });
    this.inveniasAuthService.getToken().subscribe({
      next: () => this.inveniasSigninInProgress.set(false),
      error: () => {
        this.inveniasSigninInProgress.set(false);
        const message = `The client '${tenantName}' was not found or enabled. If this keeps happening please contact Kyloe Support.`;
        this.router.navigate(['/callback'], { queryParams: { error: message } });
      },
    });
  }

  private autoSignIn(namespace: string, tenant: string): void {
    this.autoSignInInProgress = true;
    switch (namespace) {
      case 'bullhorn':   this.signInWithBullhorn(); break;
      case 'salesforce': this.signInWithSalesforce(); break;
      case 'invenias':   this.signInWithInvenias(this.lastUsedTenant ?? tenant); break;
    }
  }
}
